import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { AppShell } from '@/components/shared/AppShell'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus, Eye, Edit2, Trash2, Globe, BookOpen, FileText, ExternalLink } from 'lucide-react'

interface BlogPostItem {
  id: number
  title: string
  slug: string
  status: string
  category: string
  author_name: string
  published_at?: string
  created_at: string
  created_by: string
}

interface Stats {
  total: number
  published: number
  drafts: number
}

interface Props {
  posts: BlogPostItem[]
  stats: Stats
}

const CATEGORY_LABELS: Record<string, string> = {
  announcement:     'Announcements',
  exam_guide:       'Exam Guide',
  preparation_tips: 'Prep Tips',
  general:          'General',
}

export default function BlogPostsIndex({ posts, stats }: Props) {
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const handleDelete = (post: BlogPostItem) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return
    setDeletingId(post.id)
    router.delete(`/admin/blog_posts/${post.id}`, {
      onFinish: () => setDeletingId(null),
    })
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-border">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Blog Posts</h1>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                Content Management
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Create and manage publicly visible blog articles for student engagement and SEO.
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            <Button asChild variant="outline" size="sm" className="gap-2">
              <a href="/blog" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-3.5 h-3.5" />
                <span>Public Blog</span>
              </a>
            </Button>
            <Button asChild size="sm" className="gap-2">
              <Link href="/admin/blog_posts/new">
                <Plus className="w-4 h-4" />
                <span>New Post</span>
              </Link>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Total Posts', value: stats.total, icon: FileText },
            { label: 'Published', value: stats.published, icon: Globe },
            { label: 'Drafts', value: stats.drafts, icon: BookOpen },
          ].map(({ label, value, icon: Icon }) => (
            <Card key={label} className="border-border bg-card shadow-xs">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</p>
                  <p className="text-2xl font-black text-foreground mt-1">{value}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <Icon className="w-5 h-5" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Table */}
        <Card className="border-border bg-card shadow-xs">
          <CardHeader className="px-6 py-4 border-b border-border">
            <CardTitle className="text-base font-bold text-foreground">All Blog Posts</CardTitle>
            <CardDescription className="text-xs text-muted-foreground mt-0.5">All posts across all statuses.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-xs font-semibold uppercase text-muted-foreground border-b border-border">
                  <tr>
                    <th className="px-6 py-3">Title</th>
                    <th className="px-6 py-3">Category</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Author</th>
                    <th className="px-6 py-3">Published</th>
                    <th className="px-6 py-3">Created By</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {posts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-10 text-center text-muted-foreground">No blog posts yet.</td>
                    </tr>
                  ) : posts.map((p) => (
                    <tr key={p.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-foreground line-clamp-1 max-w-xs">{p.title}</div>
                        <div className="text-xs text-muted-foreground font-mono">/blog/{p.slug}</div>
                      </td>
                      <td className="px-6 py-4 text-xs text-muted-foreground">{CATEGORY_LABELS[p.category] || p.category}</td>
                      <td className="px-6 py-4">
                        {p.status === 'published' ? (
                          <Badge variant="default" className="text-xs font-semibold">Published</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs font-normal">Draft</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs text-muted-foreground">{p.author_name}</td>
                      <td className="px-6 py-4 text-xs text-muted-foreground">{p.published_at || '—'}</td>
                      <td className="px-6 py-4 text-xs text-muted-foreground">{p.created_by}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button asChild variant="ghost" size="sm" className="h-8 px-2.5">
                            <Link href={`/admin/blog_posts/${p.id}`}><Eye className="w-4 h-4" /></Link>
                          </Button>
                          <Button asChild variant="ghost" size="sm" className="h-8 px-2.5">
                            <Link href={`/admin/blog_posts/${p.id}/edit`}><Edit2 className="w-4 h-4" /></Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2.5 text-destructive hover:text-destructive"
                            disabled={deletingId === p.id}
                            onClick={() => handleDelete(p)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
